<template>
  <div class="flex-grow-1 d-flex flex-column overflow-y-hidden">
    <div class="a-header">
      <span class="a-header--titlegroup">
        <span class="a-header--title slide-down-enter-active">Site-Wide Incidents</span>
        <HelpButton :content="HELP_CONTENT.siteIncidents" />
      </span>
    </div>
    <div v-if="isLoading" class="d-flex justify-content-center align-items-center pt-35-px">
      <Spinner />
    </div>
    <transition name="slide-up">
      <div v-if="!isLoading" class="a-filter">
        <a-row class="full-w" type="flex" justify="start" :gutter="24">
          <a-col :span="8">
            <div class="full-w a-input-group" :class="{ active: !!filterParams.site }">
              <a-button size="large">
                <a-icon type="search" />
              </a-button>
              <a-input class="custom-prefix" placeholder="Website name" :default-value="filterParams.site" @change="onSearch">
                <a-icon v-show="isSearching" slot="suffix" class="custom-suffix" type="loading" spin />
              </a-input>
            </div>
          </a-col>
          <a-col :span="8">
            <div class="full-w a-input-group" :class="{ active: !!filterParams.from && !!filterParams.to }">
              <a-button size="large">
                <a-icon type="filter" />
              </a-button>
              <a-range-picker
                :ranges="RANGE"
                :disabledDate="disabledDate"
                :placeholder="['From', 'To']"
                :format="DATE_FORMAT.DDMMMYYYY"
                @change="onChangeDate"
              />
            </div>
          </a-col>
          <a-col :span="4">
            <div class="full-w a-select-group" :class="{ active: !!filterParams.status }">
              <a-button size="large">
                <a-icon type="flag" />
              </a-button>
              <a-select v-model="filterParams.status" :size="'large'" placeholder="Any state" allowClear @change="onChangeStatus">
                <a-select-option v-for="option in INCIDENT_STATUS_OPTIONS" :key="option.value" :value="option.value">
                  {{ option.label }}
                </a-select-option>
              </a-select>
            </div>
          </a-col>
          <a-col :span="4">
            <div class="full-w a-select-group" :class="{ active: filterParams.impactType.length > 0 }">
              <a-button size="large">
                <a-icon type="filter" />
              </a-button>
              <a-select
                v-model="filterParams.impactType"
                mode="multiple"
                :size="'large'"
                placeholder="Any category"
                :maxTagCount="1"
                :maxTagTextLength="10"
                @change="onChangeImpact"
              >
                <a-select-option v-for="option in incidentImpactOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </a-select-option>
              </a-select>
            </div>
          </a-col>
        </a-row>
      </div>
    </transition>
    <div v-resize="elSizeChange" class="a-body flex-grow-1 overflow-y-hidden">
      <ConfigBanner v-if="!isLoading" mode="incidents" :config="config" />
      <transition name="slide-up">
        <template v-if="!isLoading">
          <PaginationTable :clientPaging="false" :pagination="pagin" :columns="SiteIncidentListColumn" :dataSource="rows" :tableSize="tableSize">
            <template slot="categoryTitle">
              <HeaderHelp
                label="Category"
                lead="A site-wide incident is one of two things only."
                :items="categoryHelp"
              />
            </template>
            <template slot="windowStateTitle">
              <HeaderHelp
                label="Window State"
                lead="This says what happened to the counting window, not what the website is doing now."
                :items="windowStateHelp"
              />
            </template>
            <template slot="site" slot-scope="{ record }">
              <a-tooltip :title="'Open site-wide incident ' + record.id" placement="topLeft">
                <router-link class="ce-site" :to="'/change-events/site-incidents/' + record.id">
                  <a-icon class="ce-site__icon" type="link" />{{ record.site }}
                </router-link>
              </a-tooltip>
            </template>
            <template slot="incidentId" slot-scope="{ record }">
              <router-link :to="'/change-events/site-incidents/' + record.id">#{{ record.id }}</router-link>
            </template>
            <template slot="status" slot-scope="{ record }">
              <a-tag :color="record.status === 'active' ? 'red' : 'green'">{{ record.windowState }}</a-tag>
            </template>
            <template slot="severity" slot-scope="{ record }">
              <SeverityMix compact :severity="record.maxSeverity" :mix="record.severityMix" />
            </template>
            <template slot="openedAt" slot-scope="{ record }">
              <p class="mb-0">
                <time>{{ record.openedAt | dateString }}</time>
                <br />
                <time>{{ record.openedAt | timeString }}</time>
              </p>
            </template>
            <template slot="lastAlertAt" slot-scope="{ record }">
              <p class="mb-0">
                <time>{{ record.lastAlertAt | dateString }}</time>
                <br />
                <time>{{ record.lastAlertAt | timeString }}</time>
              </p>
            </template>
            <template slot="resolvedAt" slot-scope="{ record }">
              <p v-if="record.resolvedAt" class="mb-0">
                <time>{{ record.resolvedAt | dateString }}</time>
                <br />
                <time>{{ record.resolvedAt | timeString }}</time>
              </p>
              <span v-else>Still open</span>
            </template>
            <template slot="operation" slot-scope="{ record }">
              <a-dropdown placement="topLeft">
                <a-icon class="operation" type="ellipsis" />
                <a-menu slot="overlay" class="operation-menu">
                  <a-menu-item>
                    <a-icon type="eye" />
                    <router-link class="nav-link-detail" :to="'/change-events/site-incidents/' + record.id"> View Detail </router-link>
                  </a-menu-item>
                </a-menu>
              </a-dropdown>
            </template>
          </PaginationTable>
        </template>
      </transition>
    </div>
    <div class="a-footer">
      <PaginationBar :pageSize.sync="pagin.pageSize" :total="pagin.total" @change="paginChange" />
    </div>
  </div>
</template>

<script>
  import { debounce } from 'lodash';
  import HelpButton from './HelpButton.vue';
  import { DATE_FORMAT } from '@constants/dateFormat';
  import { indexItemTable } from '@shared/helpers/utils';
  import { SiteIncidentListColumn } from '../columnsTable/SiteIncidentListColumn';
  import ChangeEventsService from '../services/ChangeEvents.service';
  import utcDateRange from '../mixins/utcDateRange.mixin';
  import {
    INCIDENT_STATUS_OPTIONS,
    INCIDENT_STATUS_LABELS,
    CATEGORY_OPTIONS,
    labelFor,
    categoryLabel,
    severityMix,
    windowState,
    categoryHelpItems,
    WINDOW_OPEN_LABEL,
    RESOLVE_REASON_LABELS,
    durationText
  } from '../consts/ChangeEvents.consts';
  import { HELP_CONTENT } from '../consts/helpContent';
  import SeverityMix from './SeverityMix.vue';
  import ConfigBanner from './ConfigBanner.vue';
  import HeaderHelp from './HeaderHelp.vue';

  /**
   * One HIGH/MEDIUM figure from severity_counts, or 0 when the API did not
   * count them. Reading siAlertCount instead would mix two different things.
   * @param {object} row
   * @param {string} key
   * @returns {number}
   */
  function seriousCount(row, key) {
    const counts = row && row.severity_counts;
    if (!counts) return 0;
    const value = Number(counts[key]);
    return Number.isFinite(value) ? value : 0;
  }

  export default {
    name: 'SiteIncidentList',
    components: { HelpButton, SeverityMix, ConfigBanner, HeaderHelp },
    mixins: [utcDateRange],
    data() {
      let pageSize = Number(window.localStorage.getItem('pageSize'));
      if (!pageSize) pageSize = 5;

      return {
        HELP_CONTENT,
        DATE_FORMAT,
        SiteIncidentListColumn,
        INCIDENT_STATUS_OPTIONS,
        rows: [],
        // The rule that opened these windows, shown above the list. Null until
        // it arrives, and the band draws nothing rather than guessing.
        config: null,
        filterParams: {
          site: '',
          from: null,
          to: null,
          status: this.statusFromQuery(),
          impactType: []
        },
        pagin: {
          current: 1,
          pageSize: pageSize,
          total: 0
        },
        isLoading: true,
        isSearching: false,
        tableSize: {
          height: '',
          width: ''
        }
      };
    },
    computed: {
      /** What the Category heading explains on hover. */
      categoryHelp() {
        return categoryHelpItems('incidents');
      },
      /**
       * What the Window State heading explains on hover.
       *
       * The quiet period is read from the settings, not written in. If an
       * administrator changes it, this text changes with it.
       */
      windowStateHelp() {
        const quiet = durationText(this.config && this.config.auto_resolve_minutes);
        return [
          {
            name: WINDOW_OPEN_LABEL,
            meaning: 'The window remains open as alerts of this type are still occurring.'
          },
          {
            name: RESOLVE_REASON_LABELS.quiet_timeout,
            meaning: `No alerts in this window since the last ${quiet}.`
          },
          {
            name: RESOLVE_REASON_LABELS.manual,
            meaning: 'This window has been reviewed and resolved by the user.'
          }
        ];
      },
      /** A site-wide incident is only ever a defacement or an unreachable site. */
      incidentImpactOptions() {
        return CATEGORY_OPTIONS.filter((o) => o.value === 'defacement' || o.value === 'availability');
      }
    },
    mounted() {
      this.fetchList();
      this.loadConfig();
    },
    methods: {
      /**
       * The settings that opened these windows.
       *
       * Quiet on failure: the list is still correct without the band, and an
       * error about settings on a page of incidents would only confuse.
       */
      async loadConfig() {
        try {
          this.config = await ChangeEventsService.fetchConfig();
        } catch (err) {
          this.config = null;
        }
      },
      /** Honours `?status=active`, used by the Home dashboard widget. */
      statusFromQuery() {
        const query = (this.$route && this.$route.query) || {};
        const raw = Array.isArray(query.status) ? query.status[0] : query.status;
        return raw === 'active' || raw === 'resolved' ? raw : undefined;
      },
      onSearch: debounce(function (inputChangeEvent) {
        const value = inputChangeEvent.target.value;
        this.filterParams.site = value ? String(value).trim() : '';
        this.pagin.current = 1;
        this.fetchList();
      }, 800),
      async fetchList() {
        try {
          this.isSearching = true;
          const response = await ChangeEventsService.fetchSiteIncidents({
            page: this.pagin.current,
            perPage: this.pagin.pageSize,
            from: this.filterParams.from,
            to: this.filterParams.to,
            status: this.filterParams.status,
            site: this.filterParams.site,
            impactType: this.filterParams.impactType
          });
          const data = (response && response.data) || [];
          this.rows = data.map((item, index) => ({
            index: indexItemTable(this.pagin.current, this.pagin.pageSize, index),
            id: item.id,
            site: item.site || '',
            categoryLabel: categoryLabel(item),
            status: item.status,
            statusLabel: labelFor(INCIDENT_STATUS_LABELS, item.status),
            // What happened to the window: open, closed by hand, or closed by
            // the quiet timer. NOT the same thing as the website's state.
            windowState: windowState(item),
            maxSeverity: item.max_severity || 'unscored',
            // From severity_counts: how many HIGH and MEDIUM alerts the
            // incident holds. Null when the API did not count them, and the
            // cell then shows the worst severity on its own rather than zeroes.
            severityMix: severityMix(item),
            openedAt: item.opened_at,
            lastAlertAt: item.last_alert_at,
            resolvedAt: item.resolved_at,
            // The HIGH and MEDIUM alerts, and the pages carrying them - the
            // same numbers the detail page leads with. NOT siAlertCount, which
            // counts only the alerts held back from being emailed and so read
            // "2 alerts on 7 pages", which cannot happen.
            alertCount: seriousCount(item, 'serious'),
            pageCount: seriousCount(item, 'serious_pages'),
            eventCount: item.event_count == null ? 0 : item.event_count
          }));
          this.pagin.total = (response && response.total) || 0;
          this.isSearching = false;
        } catch (err) {
          this.isSearching = false;
          this.$showMultiErrorMessage(err.messages);
        } finally {
          if (this.isLoading) {
            setTimeout(() => {
              this.isLoading = false;
            }, 300);
          }
        }
      },
      onChangeStatus(value) {
        this.filterParams.status = value;
        this.pagin.current = 1;
        this.fetchList();
      },
      onChangeImpact(values) {
        this.filterParams.impactType = Array.isArray(values) ? values : [];
        this.pagin.current = 1;
        this.fetchList();
      },
      onChangeDate(dates, dateStrings) {
        this.filterParams.from = dateStrings[0] ? this.getFilteredUTCStartDate(dateStrings[0], 'DD MMM YYYY') : null;
        this.filterParams.to = dateStrings[1] ? this.getFilteredUTCEndDate(dateStrings[1], 'DD MMM YYYY') : null;
        this.pagin.current = 1;
        this.fetchList();
      },
      paginChange(pagin) {
        this.pagin = { ...pagin };
        this.fetchList();
      },
      elSizeChange(size) {
        this.tableSize = size;
      }
    }
  };
</script>

<style lang="scss" scoped>
  .custom-suffix {
    font-size: 20px;
    color: #20a4b8;
  }
  .nav-link-detail {
    display: inline-block;
    width: 100%;
    &:hover {
      text-decoration: underline;
    }
  }
  // The website name opens the incident. It used to inherit the body colour,
  // so it read as plain text and nobody knew it could be clicked.
  .ce-site {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #20a4b8;
    font-weight: 600;

    &__icon {
      margin-right: 5px;
      font-size: 11px;
      opacity: 0.75;
    }
    &:hover {
      color: #17808f;
      text-decoration: underline;
    }
  }
</style>
